import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import Button from 'components/Button';
import BACKEND_URL from 'constants/backendUrl';
import { POST } from 'utils/http';
import { RadioImageField, TextInput } from '../lib';
import EventMood from 'core/enums/eventMood';
import { useModalStore } from 'stores/modal';
import { DisabledField, ModalType } from 'core/useCases/modal/types';
import { useInvalidateContacts } from 'core/api/contacts';
import { useEventById, useInvalidateEvents } from 'core/api/events';
import Event from 'core/models/event';
import { useInvalidateTasks, useTaskById } from 'core/api/tasks';
import Task from 'core/models/task';
import Mood1 from 'images/icons/Mood1.svg';
import Mood2 from 'images/icons/Mood2.svg';
import Mood3 from 'images/icons/Mood3.svg';
import Mood4 from 'images/icons/Mood4.svg';
import ActivityType from 'core/enums/activityType';
import { useInvalidateApplications } from 'core/api/applications';
import ResourcesInput from '../lib/ResourcesInput';

type Inputs = {
    comment: string;
    mood?: EventMood;
    resources?: string;
};

type AddActivityReportResponse = {
    activity: Event | Task;
    automaticTasks: Task[];
};

const addActivityReportRequest = async (
    inputs: Inputs,
    id: string,
    activityType: ActivityType,
): Promise<AddActivityReportResponse> => {
    const response = (await POST<AddActivityReportResponse>(`${BACKEND_URL}/api/activity/add-report`, {
        ...inputs,
        activityType,
        id,
    })) as AddActivityReportResponse;

    return response;
};

type UpdateActivityReportResponse = {
    activity: Event | Task;
};

const updateActivityReportRequest = async (
    inputs: Inputs,
    id: string,
    activityType: ActivityType,
): Promise<UpdateActivityReportResponse> => {
    const response = (await POST<UpdateActivityReportResponse>(`${BACKEND_URL}/api/activity/update-report`, {
        ...inputs,
        activityType,
        id,
    })) as UpdateActivityReportResponse;

    return response;
};

type ActivityReportFormProps = {
    closeModal(): void;
    data: {
        id: string;
        activityType: ActivityType;
        isAlreadyCompleted: boolean;
        comment: string;
        mood?: EventMood;
    };
};

const ActivityReportForm: FC<ActivityReportFormProps> = ({ closeModal, data }) => {
    const { t } = useTranslation();
    const openModal = useModalStore((s) => s.openModal);
    const closeModalStore = useModalStore((s) => s.closeModal);
    const invalidateTasks = useInvalidateTasks();
    const invalidateEvents = useInvalidateEvents();
    const invalidateContacts = useInvalidateContacts();
    const invalidateApplications = useInvalidateApplications();
    const { id, activityType, isAlreadyCompleted, comment, mood } = data;
    const recoveredTask = useTaskById(id).data;
    const recoveredEvent = useEventById(id).data;

    const methods = useForm<Inputs>({
        defaultValues: { comment, mood },
    });
    const { handleSubmit } = methods;

    const openNewTaskModal = (activity: Event | Task) => {
        const { contact, application } = activity;
        const disabledFields: Array<DisabledField> = [];
        if (application) {
            disabledFields.push(DisabledField.ApplicationId);
        } else if (contact) {
            disabledFields.push(DisabledField.ContactId);
        }
        openModal({
            allowSwitchToEvent: true,
            cancelButtonLabel: t('common.ignore'),
            defaultValues: {
                application,
                contact,
            },
            disabledFields,
            title: t('task.new-task-modal-title'),
            type: ModalType.Task,
        });
    };

    const updateActivityResourcesRequest = async (
        inputs: Inputs,
        ActivityId: string,
        activityTypes: ActivityType,
    ): Promise<void> => {
        (await POST(`${BACKEND_URL}/api/activity/update-additional-information`, {
            activityType: activityTypes,
            id: ActivityId,
            inputs,
        })) as Event | Task;
    };

    const onSubmit = handleSubmit(async (values: Inputs) => {
        try {
            if (values.resources) {
                await updateActivityResourcesRequest(values, id, activityType);
            }
            if (isAlreadyCompleted) {
                const { activity } = await updateActivityReportRequest(values, id, activityType);
                if (ActivityType.Task === activityType) {
                    invalidateTasks();
                } else {
                    invalidateEvents();
                }
                closeModalStore();
            } else {
                const { activity, automaticTasks } = await addActivityReportRequest(values, id, activityType);
                if (ActivityType.Task === activityType) {
                    invalidateTasks();
                } else {
                    invalidateEvents();
                }
                if (activity?.contact) {
                    invalidateContacts();
                }
                if (activity?.application) {
                    invalidateApplications();
                }
                if (automaticTasks) {
                    invalidateTasks();
                }
                closeModalStore();
                openNewTaskModal(activity);
            }
            toast.success(t(`activity.report.${isAlreadyCompleted ? 'update' : 'add'}.success`));
        } catch (e) {
            toast.error(t(`activity.report.${isAlreadyCompleted ? 'update' : 'add'}.error`));
        }
    });

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                {ActivityType.Event === activityType && (
                    <RadioImageField
                        choices={[
                            { image: Mood1, title: t('common.mood.enum.missed'), value: EventMood.Missed },
                            { image: Mood2, title: t('common.mood.enum.poor'), value: EventMood.Poor },
                            { image: Mood3, title: t('common.mood.enum.satisfying'), value: EventMood.Satisfying },
                            { image: Mood4, title: t('common.mood.enum.excellent'), value: EventMood.Excellent },
                        ]}
                        label={t('common.what-mood-after-interview')}
                        name="mood"
                        required
                    />
                )}
                <TextInput
                    label={t('common.how-the-process-was')}
                    name="comment"
                    placeholder={t('common.write-a-comment')}
                    isTextArea
                />
                <ResourcesInput event={recoveredEvent} name="resources" task={recoveredTask} />
                <BaseModal.BottomActions>
                    <Button onClick={() => closeModal()} variant="text">
                        {t('common.cancel')}
                    </Button>
                    <Button submit>{t('common.confirm')}</Button>
                </BaseModal.BottomActions>
            </form>
        </FormProvider>
    );
};

export default ActivityReportForm;
